import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight, CheckCircle2, ExternalLink, Calendar,
  Users, Globe, ShieldCheck, BadgeEuro, MessagesSquare, Smartphone
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext
} from '../../components/ui/carousel';
import Layout from '../../components/layout/Layout';
import SEO, { SEO_DATA } from '../../components/SEO';
import { PRODUCTS } from '../../lib/constants';
import { generateBreadcrumbSchema, generateProductSchema } from '../../lib/schemas';

const fadeInUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } } };
const staggerContainer = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.05 } } };

const FEATURE_ICONS = [Users, Globe, ShieldCheck, BadgeEuro, MessagesSquare, Smartphone];

const ProductDetail = () => {
  const { productId } = useParams();
  const reduceMotion = useReducedMotion();
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  const product = PRODUCTS.find(p => p.id === productId);

  // Solo los productos con landing interna (campo `hero`) renderizan página.
  if (!product || !product.hero) return <Navigate to="/productos" replace />;

  const accent = product.accentColor || '#2563EB';
  const path = `/productos/${productId}`;
  const seoEntry = SEO_DATA[path] || {};
  // OG image propia del producto (URL absoluta para scrapers sociales / IA)
  const ogImage = product.logo
    ? (product.logo.startsWith('http') ? product.logo : `https://www.leapifyes.com${product.logo}`)
    : undefined;

  // En móvil (o con reduced-motion) se DESACTIVAN las animaciones de scroll:
  // el contenido se renderiza estático → cero jank. En desktop se mantienen.
  const useAnim = !reduceMotion && !isMobile;
  const sectionAnim = useAnim
    ? { initial: 'hidden', whileInView: 'visible', viewport: { once: true, amount: 0.2 }, variants: staggerContainer }
    : {};
  const itemAnim = (delay = 0) =>
    useAnim
      ? { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.2 }, transition: { delay } }
      : {};

  const pageSchemas = [
    generateBreadcrumbSchema([
      { name: 'Inicio', path: '/' },
      { name: 'Productos', path: '/productos' },
      { name: product.name, path },
    ]),
    generateProductSchema({ product, path }),
  ];

  return (
    <Layout>
      {/* overflow-x-clip: blinda la página contra cualquier desbordamiento horizontal
          en móvil (no afecta al scroll vertical). */}
      <div className="overflow-x-clip">
      <SEO
        title={seoEntry.title || `${product.name} | Leapifyes`}
        description={seoEntry.description || product.hero.subtitle}
        keywords={seoEntry.keywords}
        path={path}
        image={ogImage}
        schema={pageSchemas}
      />

      {/* HERO */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="product-hero">
        <div className="absolute inset-0">
          <div className="mesh-gradient opacity-40" />
          <div className="grid-pattern absolute inset-0 opacity-20" />
        </div>
        <div className="container-main relative z-10">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 glass-card border rounded-full mb-6" style={{ borderColor: `${accent}33` }}>
              <span className="w-2 h-2 rounded-full" style={{ background: accent }} />
              <span className="text-sm font-medium" style={{ color: accent }}>{product.hero.eyebrow}</span>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex items-center gap-4 mb-6">
              <img
                src={product.logo}
                alt={product.name}
                loading="eager"
                className="w-16 h-16 rounded-2xl object-cover flex-shrink-0"
                style={{ boxShadow: `0 0 30px ${accent}40` }}
              />
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-[#F0F4FF] leading-tight">
                {product.hero.title}
              </h1>
            </motion.div>

            <motion.p variants={fadeInUp} className="text-lg text-[#8892A4] mb-8 leading-relaxed max-w-2xl">
              {product.hero.subtitle}
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-4">
              {/* Badge oficial App Store */}
              <a href={product.appStoreUrl} target="_blank" rel="noopener noreferrer" aria-label={`Descargar ${product.name} en el App Store`}>
                <img
                  src="/appmando/app-store-es.svg"
                  alt="Descárgalo en el App Store"
                  loading="eager"
                  className="h-[52px] w-auto"
                />
              </a>
              <a href="https://appmando.com" target="_blank" rel="noopener noreferrer">
                <Button className="btn-secondary text-base px-7 py-6" data-testid="product-cta-web">
                  Ver appmando.com <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </motion.div>

            {/* Google Play — Próximamente */}
            <motion.div variants={fadeInUp} className="mt-4">
              <span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-[#8892A4] border border-white/10 bg-white/5 opacity-70 cursor-not-allowed select-none"
                aria-disabled="true"
              >
                <Smartphone className="w-4 h-4" /> Google Play · Próximamente
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* GALERÍA — carrusel de capturas */}
      <section className="section-padding border-t border-white/5" data-testid="product-gallery">
        <div className="container-main">
          <motion.div {...sectionAnim} className="text-center mb-12">
            <motion.span variants={fadeInUp} className="font-semibold mb-4 block uppercase tracking-wider text-sm" style={{ color: accent, fontFamily: 'Space Grotesk, sans-serif' }}>La app por dentro</motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-[#F0F4FF]">
              Diseñada para <span className="gradient-text">resolver</span>
            </motion.h2>
          </motion.div>

          <Carousel opts={{ align: 'start', loop: true }} className="w-full max-w-5xl mx-auto px-4 sm:px-12">
            <CarouselContent>
              {product.gallery.map((shot, i) => (
                <CarouselItem key={i} className="basis-4/5 sm:basis-1/2 lg:basis-1/3">
                  <div className="glass-card p-3 h-full">
                    <img
                      src={shot.src}
                      alt={shot.alt}
                      loading="lazy"
                      width={1284}
                      height={2778}
                      className="w-full rounded-2xl object-cover"
                      style={{ aspectRatio: '1284 / 2778' }}
                    />
                    <p className="text-sm text-[#8892A4] text-center mt-3 px-2">{shot.alt}</p>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex text-[#F0F4FF] border-white/15 bg-white/5 hover:bg-white/10" />
            <CarouselNext className="hidden sm:flex text-[#F0F4FF] border-white/15 bg-white/5 hover:bg-white/10" />
          </Carousel>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section-padding border-t border-white/5" data-testid="product-features">
        <div className="container-main">
          <motion.div {...sectionAnim} className="text-center mb-16">
            <motion.span variants={fadeInUp} className="font-semibold mb-4 block uppercase tracking-wider text-sm" style={{ color: accent, fontFamily: 'Space Grotesk, sans-serif' }}>Qué la hace distinta</motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-[#F0F4FF]">
              Todo lo que necesitas para <span className="gradient-text">tu hogar</span>
            </motion.h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.features.map((feature, i) => {
              const FIcon = FEATURE_ICONS[i % FEATURE_ICONS.length];
              return (
                <motion.div key={i} {...itemAnim(i * 0.06)} className="glass-card-hover p-8">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: `${accent}18` }}>
                    <FIcon className="w-7 h-7" style={{ color: accent }} />
                  </div>
                  <h3 className="text-xl font-bold text-[#F0F4FF] mb-3">{feature.title}</h3>
                  <p className="text-[#8892A4] leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="section-padding border-t border-white/5" data-testid="product-process">
        <div className="container-main">
          <motion.div {...sectionAnim} className="max-w-3xl mb-16">
            <motion.span variants={fadeInUp} className="font-semibold mb-4 block uppercase tracking-wider text-sm" style={{ color: accent, fontFamily: 'Space Grotesk, sans-serif' }}>Cómo funciona</motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-[#F0F4FF]">
              Del problema a la <span className="gradient-text">solución</span>
            </motion.h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {product.process.map((step, i) => (
              <motion.div key={i} {...itemAnim(i * 0.12)} className="glass-card p-8 relative group hover:border-white/20 transition-colors">
                <span className="text-6xl font-black absolute top-2 right-4" style={{ color: `${accent}1A` }}>{step.step}</span>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${accent}18` }}>
                  <span className="font-bold" style={{ color: accent }}>{step.step}</span>
                </div>
                <h3 className="text-lg font-bold text-[#F0F4FF] mb-2">{step.title}</h3>
                <p className="text-sm text-[#8892A4]">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="section-padding border-t border-white/5" data-testid="product-benefits">
        <div className="container-main">
          <motion.div {...sectionAnim} className="text-center mb-16">
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-[#F0F4FF]">
              Gana <span className="gradient-text">cada lado</span>
            </motion.h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {product.benefits.map((benefit, i) => (
              <motion.div key={i} {...itemAnim(i * 0.08)} className="glass-card p-6 flex items-start gap-4">
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: accent }} />
                <span className="text-[#F0F4FF]">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="section-padding border-t border-white/5" data-testid="product-stats">
        <div className="container-main">
          <div className="grid md:grid-cols-4 gap-6">
            {product.stats.map((stat, i) => (
              <motion.div key={i} {...itemAnim(i * 0.1)} className="text-center glass-card p-6">
                <p className="text-4xl md:text-5xl font-black gradient-text mb-2">{stat.value}</p>
                <p className="text-[#8892A4]">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="section-padding relative overflow-hidden" data-testid="product-cta-final">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B93A4]/10 via-transparent to-[#D946EF]/10" />
        <div className="container-main relative z-10">
          <motion.div {...itemAnim()} className="glass-card p-12 md:p-16 text-center" style={{ borderColor: `${accent}33` }}>
            <h2 className="text-3xl md:text-4xl font-black text-[#F0F4FF] mb-4">
              No solo lo asesoramos. <span className="gradient-text">Lo construimos.</span>
            </h2>
            <p className="text-lg text-[#8892A4] mb-8 max-w-2xl mx-auto">
              Leapifyes no solo asesora la transformación digital: construye y opera sus propios productos. AppMando es la prueba — nuestra primera aplicación nativa, viva en las tiendas.
            </p>
            <div className="flex flex-wrap gap-4 justify-center items-center">
              <a href={product.appStoreUrl} target="_blank" rel="noopener noreferrer" aria-label={`Descargar ${product.name} en el App Store`}>
                <img src="/appmando/app-store-es.svg" alt="Descárgalo en el App Store" loading="lazy" className="h-[52px] w-auto" />
              </a>
              <Link to="/contacto">
                <Button className="btn-gradient text-base px-8 py-6" data-testid="product-cta-contacto">
                  <Calendar className="w-5 h-5 mr-2" /> Agendar diagnóstico <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      </div>
    </Layout>
  );
};

export default ProductDetail;
